import {RootState} from '../../..';
import {ReservationOwner, WorkspaceReservation} from '../apiTypes';

export type Attendee = {
  reservation: WorkspaceReservation;
  owner: ReservationOwner;
  isOrganizer: boolean;
  isCurrentUser: boolean;
};

export type ReservationDetails =
  | {status: 'pending'}
  | {status: 'rejected'}
  | {status: 'fulfilled'; data: Array<Attendee>};

const LOADING = {status: 'pending'} as const;
const REJECTED = {status: 'rejected'} as const;

const statusImportance: Record<WorkspaceReservation['status'], number> = {
  Confirmed: 0,
  InvitationPending: 1,
  InvitationDeclined: 2,
  Cancelled: 3,
  Denied: 3,
};

/**
 * Returns all attendees for a workspace reservation, with computed isOrganizer, isCurrentUser, and sorted by role and status.
 * Merges invitation and attendee data into a single list with unified status for UI.
 */
export const getAttendeeOverview = (
  state: RootState,
  workspaceReservation?: WorkspaceReservation | null,
): ReservationDetails => {
  if (!workspaceReservation) return {data: [], status: 'fulfilled'};

  const members = Array<Attendee>();

  const parentId = workspaceReservation.hasInvitations ? workspaceReservation.id : workspaceReservation.invitationTo;

  // Solo
  const soloMember: Attendee = {
    reservation: workspaceReservation,
    isOrganizer: workspaceReservation.owner.id === state.data.user.user.id,
    owner: {
      id: state.data.user.user.id,
      name: `${state.data.user.user.firstName} ${state.data.user.user.lastName}`,
      initials: state.data.user.user.initials,
      imageHash: state.data.user.user.imageHash ?? '',
      email: state.data.user.user.email,
    },
    isCurrentUser: true,
  };

  if (!parentId) {
    return {
      status: 'fulfilled' as const,
      data: [soloMember],
    };
  }

  // Get invitations
  const invitationsEntry = state.data.timeslots.childReservations[parentId];
  if (invitationsEntry?.status === 'rejected') {
    return REJECTED;
  }

  if (!invitationsEntry || invitationsEntry.status === 'pending') {
    return LOADING;
  }

  for (const invitation of invitationsEntry.data) {
    members.push({
      reservation: invitation,
      // TODO: parent seems to be in invitations list
      isOrganizer: invitation.id === parentId,
      owner: invitation.owner,
      isCurrentUser: invitation.owner.id === state.data.user.user.id,
    });
  }

  // Sort members
  members.sort((a, b) => {
    if (a.isOrganizer) return -1;
    if (b.isOrganizer) return 1;

    // Group different statuses
    const statusOrder = statusImportance[a.reservation.status] - statusImportance[b.reservation.status];
    if (statusOrder) return statusOrder;

    // Within status groups, sort alphabetically
    return a.owner.name.localeCompare(b.owner.name);
  });

  return {
    status: 'fulfilled' as const,
    data: members,
  };
};
